import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";

const DemoContext = createContext(null);

const DEMO_SESSION_KEY = "demo_mode";
export const DEMO_THEME_SWITCHED_EVENT = "demo:theme-switched";
const DEMO_EXPIRY_KEY = "demo_expires_at";
const SESSION_DURATION_MS = 30 * 60 * 1000; // 30 minutes
let startInFlight = null;

export function DemoProvider({ children }) {
  const [isDemoMode, setIsDemoMode] = useState(() => localStorage.getItem(DEMO_SESSION_KEY) === "true");
  const [demoTheme, setDemoTheme] = useState(() => localStorage.getItem("demo_theme") || "bounce_house");
  const [timeLeft, setTimeLeft] = useState(0);
  const [switching, setSwitching] = useState(false);
  const queryClient = useQueryClient();
  const timerRef = useRef(null);

  // Timer countdown
  useEffect(() => {
    if (!isDemoMode) return;
    const expiresAt = parseInt(localStorage.getItem(DEMO_EXPIRY_KEY) || "0", 10);
    if (!expiresAt) return;

    const tick = () => {
      const remaining = Math.max(0, expiresAt - Date.now());
      setTimeLeft(remaining);
      if (remaining <= 0) {
        endSession();
      }
    };
    tick();
    timerRef.current = setInterval(tick, 1000);
    return () => clearInterval(timerRef.current);
  }, [isDemoMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const startSession = useCallback(async () => {
    // Dedupe: StrictMode / re-mounts fired this twice, racing two demo resets → "Booking not found" mid-session.
    if (startInFlight) return startInFlight;
    startInFlight = (async () => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
    const res = await fetch(`${backendUrl}/api/demo/start-session`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    });
    if (!res.ok) throw new Error("Failed to start demo session");
    const data = await res.json();

    // Set auth tokens (same as regular login); drop any site selection left by a previous tenant.
    localStorage.removeItem("admin_selected_microsite_id");
    localStorage.setItem("auth_token", data.access_token);
    localStorage.setItem("refresh_token", data.refresh_token);

    // Set demo flags
    localStorage.setItem(DEMO_SESSION_KEY, "true");
    localStorage.setItem(DEMO_EXPIRY_KEY, String(Date.now() + SESSION_DURATION_MS));
    localStorage.setItem("demo_theme", data.business_type || "bounce_house");

    setIsDemoMode(true);
    setDemoTheme(data.business_type || "bounce_house");
    return data;
    })().finally(() => { startInFlight = null; });
    return startInFlight;
  }, []);

  const switchTheme = useCallback(async (businessType) => {
    setSwitching(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
      const res = await fetch(`${backendUrl}/api/demo/switch-theme`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem("auth_token")}`,
        },
        body: JSON.stringify({ business_type: businessType }),
      });
      if (!res.ok) throw new Error("Failed to switch theme");
      localStorage.setItem("demo_theme", businessType);
      setDemoTheme(businessType);
      // Catalog, bookings, pages and theme all changed server-side — refresh every open screen.
      queryClient.invalidateQueries();
      window.dispatchEvent(new CustomEvent(DEMO_THEME_SWITCHED_EVENT, { detail: { businessType } }));
    } finally {
      setSwitching(false);
    }
  }, [queryClient]);

  const endSession = useCallback(() => {
    localStorage.removeItem(DEMO_SESSION_KEY);
    localStorage.removeItem(DEMO_EXPIRY_KEY);
    localStorage.removeItem("demo_theme");
    localStorage.removeItem("auth_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("auth_user");
    localStorage.removeItem("app_mode");
    localStorage.removeItem("admin_selected_microsite_id");
    setIsDemoMode(false);
    setDemoTheme("bounce_house");
    setTimeLeft(0);
    if (timerRef.current) clearInterval(timerRef.current);
  }, []);

  const formatTimeLeft = useCallback(() => {
    const mins = Math.floor(timeLeft / 60000);
    const secs = Math.floor((timeLeft % 60000) / 1000);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  }, [timeLeft]);

  return (
    <DemoContext.Provider value={{
      isDemoMode,
      demoTheme,
      timeLeft,
      switching,
      startSession,
      switchTheme,
      endSession,
      formatTimeLeft,
    }}>
      {children}
    </DemoContext.Provider>
  );
}

export function useDemo() {
  const ctx = useContext(DemoContext);
  if (!ctx) throw new Error("useDemo must be inside DemoProvider");
  return ctx;
}
